/**
 * Recorded-URL scrubbing for Ryn Replay: the query-parameter allowlist and the
 * credential path-segment redaction applied to every URL the recorder stores.
 * Split out of ryn-replay-masking-policy.ts (500-line gate) and re-exported
 * from there, so importers are unchanged.
 */

import {
  hasRynReplayOpaquePart,
  redactRynReplayOpaqueRuns,
} from './ryn-replay-opaque-segment';

export { isRynReplayOpaqueSegment } from './ryn-replay-opaque-segment';

/**
 * Query parameters whose values are safe to keep in a recorded URL.
 *
 * An allowlist, not a blocklist, and that direction is the whole point. A
 * blocklist only redacts the sensitive names somebody thought of, so the first
 * parameter nobody predicted gets stored in full. With an allowlist the unknown
 * case is redacted and the cost of being wrong is a slightly less useful debug
 * URL instead of a credential in storage.
 *
 * These names are here because they tell a support engineer which view the user
 * was on and nothing about who they are.
 */
export const RYN_REPLAY_SAFE_URL_PARAMS: readonly string[] = [
  'tab',
  'page',
  'pageSize',
  'sort',
  'sortBy',
  'dir',
  'view',
  'filter',
  'status',
  'lang',
];

/** What a redacted parameter value is replaced with. */
export const RYN_REPLAY_REDACTED = '[redacted]';

/**
 * The origin-less base a relative URL is parsed against so the WHATWG `URL`
 * parser accepts it. Never appears in output: it is sliced back off. Shared by
 * the URL scrub and the path-shape probe so both parse relative URLs the same.
 */
export const RYN_REPLAY_URL_PLACEHOLDER_BASE = 'https://ryn-replay.invalid';

/**
 * Path segments that mark a KNOWN credential or capability route. The segment
 * IMMEDIATELY AFTER any of these is a live secret carried in the URL path (which
 * the query+fragment scrub never touched), so it is redacted in EVERY app with
 * no per-app config; the route prefix survives so a viewer still sees the page.
 * Bug #33910.
 *
 *   - password-reset : reset code AND InitialSet activation link,
 *     `public/password-reset/:code` (web-employee-packet).
 *   - training-record: `public/training-record/:employeeId`, unauthenticated
 *     capability URL (web-safety).
 *   - shared-territory: `shared-territory/:token`, public no-login capability
 *     token (web-operations-management).
 *   - territory-share: `api/public/territory-share/:token`, the API call that
 *     page makes with the SAME token, recorded by network capture (#33943).
 *
 * Matches a WHOLE segment case-insensitively, never a substring.
 */
export const RYN_REPLAY_CREDENTIAL_PATH_MARKERS: readonly string[] = [
  'password-reset',
  'training-record',
  'shared-territory',
  'territory-share',
];

/** Percent-decode a single segment for marker matching; never throws. */
const safeDecodeSegment = (segment: string): string => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

let opaqueSegmentRedaction = false;

/**
 * The per-app opt-in (`environment.rynReplay.redactOpaquePathSegments`, WI
 * 34328), set once by the recorder provider for the app that records. When on,
 * every URL scrub (page, network, console text, performance) also redacts
 * opaque segments, so a new credential route does not leak while it waits for
 * a marker. The markers above stay on either way.
 */
export const setRynReplayOpaqueSegmentRedaction = (enabled: boolean): void => {
  opaqueSegmentRedaction = enabled;
};

/**
 * Redact secret PATH segments: the segment following any known credential
 * marker is replaced, and the route prefix is kept. Returns the SAME string by
 * reference-equality when nothing matched, so an untouched URL round-trips
 * byte-for-byte and every existing non-credential path survives.
 */
export const scrubRynReplayPathname = (pathname: string): string => {
  const segments = pathname.split('/');
  let afterMarker = false;
  let changed = false;
  for (let i = 0; i < segments.length; i++) {
    const raw = segments[i];
    if (!raw) {
      // Empty segment (leading slash, doubled slash): skipped WITHOUT closing the
      // marker window, so `/password-reset//<code>` still redacts the code. Fail
      // closed: the secret is the next non-empty segment (#34844).
      continue;
    }
    if (afterMarker) {
      segments[i] = RYN_REPLAY_REDACTED;
      changed = true;
      afterMarker = false;
      continue;
    }
    // Compare the route PATH only. Angular binds `public/password-reset;view=x/:code`
    // (matrix params follow a LITERAL ';' in the segment), so match the part
    // before the first literal ';', and decode after splitting: an encoded %3B is
    // part of the segment name, exactly as Angular's serializer treats it.
    const name = safeDecodeSegment(raw.split(';', 1)[0]);
    afterMarker = RYN_REPLAY_CREDENTIAL_PATH_MARKERS.includes(
      name.toLowerCase()
    );
    if (opaqueSegmentRedaction && hasRynReplayOpaquePart(raw)) {
      // A marker segment keeps its name and the marker stays armed; only its
      // matrix part is opaque (`password-reset;code=<tok>`, PR 4516 review 6).
      const matrix = raw.indexOf(';');
      segments[i] =
        afterMarker && matrix >= 0
          ? `${raw.slice(0, matrix)};${RYN_REPLAY_REDACTED}`
          : RYN_REPLAY_REDACTED;
      changed = true;
    }
  }
  return changed ? segments.join('/') : pathname;
};

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * A credential marker as a path segment inside free text: preceded by a path or
 * text boundary, optional matrix params, one or more slashes, then the secret
 * segment. Groups: 1 boundary, 2 marker, 3 matrix params, 4 slashes, 5 secret.
 * A percent-encoded slash (`%2F`, a returnUrl) counts as a slash on both sides
 * of the marker, and `,` or `\` count as boundaries (#34844 review). The secret
 * stops before a closing `]` or backtick so the delimiter stays readable.
 */
const CREDENTIAL_PATH_IN_TEXT = new RegExp(
  `(^|[/\\s'"(=,\\\\]|%2F)(${RYN_REPLAY_CREDENTIAL_PATH_MARKERS.map(
    escapeRegExp
  ).join('|')})((?:;[^/\\s'"]*)?)((?:/|%2F)+)((?:(?!%2F)[^/\\s?#'"<>)\\]\`])+)`,
  'gi'
);

/**
 * The path-marker rule applied to FREE TEXT (#34844): an HttpErrorResponse
 * message or a Router NavigationError names the request URL or route segment,
 * token included, so the secret segment after any credential marker is
 * redacted wherever it appears in the string. A marker word that is not a path
 * segment (no following `/`) is left alone. Returns the SAME string by
 * reference when nothing matched.
 */
export const scrubRynReplayCredentialPathsInText = (text: string): string => {
  if (!text) {
    return text;
  }
  const scrubbed = text.replace(
    CREDENTIAL_PATH_IN_TEXT,
    (
      _match,
      boundary: string,
      marker: string,
      matrix: string,
      slashes: string,
      secret: string
    ) => {
      // Sentence punctuation after the segment ("<url>: 500") goes back to the
      // text; anything inside the segment, ':' included, stays redacted.
      const trailing = /[:.,;]+$/.exec(secret)?.[0] ?? '';
      return `${boundary}${marker}${matrix}${slashes}${RYN_REPLAY_REDACTED}${trailing}`;
    }
  );
  return scrubbed === text ? text : scrubbed;
};

/**
 * One `?key=value` or `&key=value` pair inside free text. Groups: 1 separator,
 * 2 key, 3 value. The value stops at whitespace, quotes and brackets, so an
 * already-redacted `[redacted]` value is an empty match and is left alone.
 */
const QUERY_PARAM_IN_TEXT = /([?&])([^=&#?\s'"<>]+)=([^&#\s'"<>()[\]`]*)/g;

/**
 * The query allowlist applied to FREE TEXT (#34844 review): an HttpErrorResponse
 * message and stack name the full request URL, so a `sig` or `token` value
 * survived there while the `url` key beside it was redacted. Same allowlist and
 * same key-kept, value-replaced rule as {@link scrubRynReplayUrl}. Returns the
 * SAME string by reference when nothing matched.
 */
export const scrubRynReplayQueryValuesInText = (
  text: string,
  safeParams: readonly string[] = RYN_REPLAY_SAFE_URL_PARAMS
): string => {
  if (!text) {
    return text;
  }
  const allowed = new Set(safeParams.map((p) => p.toLowerCase()));
  const scrubbed = text.replace(
    QUERY_PARAM_IN_TEXT,
    (match, separator: string, key: string, value: string) => {
      if (!value || allowed.has(key.toLowerCase())) {
        return match;
      }
      const trailing = /[:.,;]+$/.exec(value)?.[0] ?? '';
      return `${separator}${key}=${RYN_REPLAY_REDACTED}${trailing}`;
    }
  );
  return scrubbed === text ? text : scrubbed;
};

/** Parse just the pathname out of an absolute or relative URL, or undefined. */
export const parseUrlPathname = (value: string): string | undefined => {
  try {
    return new URL(value).pathname;
  } catch {
    try {
      return new URL(value, RYN_REPLAY_URL_PLACEHOLDER_BASE).pathname;
    } catch {
      return undefined;
    }
  }
};

/**
 * Strip sensitive values out of a URL before it is recorded.
 *
 * Why this exists: the recorder stamps the page URL onto every event, and that
 * URL becomes the session's LandingUrl in SQL as well. Measured on a real
 * recording, 37 of 236 events carried the Auth0 callback URL complete with the
 * `code` and `state` parameters (bug #30106). The authorization code itself is
 * spent by the time anyone watches the replay, so that specific value was not
 * the live risk. The default was.
 *
 * A screen recorder that keeps whatever is in the query string will eventually
 * keep a password reset token, a shared access signature, or somebody's email
 * address, and it will do it silently. So values are redacted unless the
 * parameter name is known to be safe.
 *
 * The KEY is kept and only the VALUE is replaced. Knowing a `code` parameter was
 * present is exactly the context that explains why the page did what it did, and
 * dropping the key entirely would hide that.
 *
 * The fragment goes completely. It is never sent to a server, it is a common
 * place for tokens in implicit auth flows, and it carries no routing information
 * this product needs.
 */
export const scrubRynReplayUrl = (
  url: string,
  safeParams: readonly string[] = RYN_REPLAY_SAFE_URL_PARAMS
): string => {
  if (!url) {
    return '';
  }

  // Relative URLs are legitimate and must survive. A base is supplied only so
  // the parser accepts them, then removed again below.
  let parsed: URL;
  let wasRelative = false;
  try {
    parsed = new URL(url);
  } catch {
    try {
      parsed = new URL(url, RYN_REPLAY_URL_PLACEHOLDER_BASE);
      wasRelative = true;
    } catch {
      // Not parseable at all. Returning the raw string would defeat the whole
      // guard, so give up the URL rather than the redaction.
      return RYN_REPLAY_REDACTED;
    }
  }

  const allowed = new Set(safeParams.map((p) => p.toLowerCase()));
  // Collected via forEach, then mutated, for two reasons: `keys()` needs the
  // `dom.iterable` lib this platform-neutral library cannot assume, and
  // mutating the params while iterating them is undefined either way.
  const presentKeys: string[] = [];
  parsed.searchParams.forEach((_value, key) => {
    presentKeys.push(key);
  });
  for (const key of presentKeys) {
    if (!allowed.has(key.toLowerCase())) {
      parsed.searchParams.set(key, RYN_REPLAY_REDACTED);
    }
  }

  parsed.hash = '';

  // Redact secrets carried in the PATH (reset/activation tokens, capability
  // ids). Only reassign when a segment actually changed, so an untouched path
  // is not needlessly re-encoded by the pathname setter. Bug #33910.
  const scrubbedPath = scrubRynReplayPathname(parsed.pathname);
  if (scrubbedPath !== parsed.pathname) {
    parsed.pathname = scrubbedPath;
  }

  const out = parsed.toString();
  return wasRelative ? out.slice(RYN_REPLAY_URL_PLACEHOLDER_BASE.length) : out;
};

/**
 * API calls that carry a page's capability secret, keyed by the PUBLIC page
 * that makes them (#34327). The unauthenticated `public/training-record/:employeeId`
 * page calls these two APIs with the same employee id. They are redacted only
 * while the recorded page is that public page: a bare `training-records` or
 * `dispatch` marker would blank the ids the authenticated screens and every
 * ordinary dispatch call legitimately carry. Each entry: the public page
 * segment after `public/`, and the API path prefixes whose NEXT segment is the
 * secret.
 */
export const RYN_REPLAY_PUBLIC_PAGE_SECRET_APIS: readonly {
  page: string;
  apiPrefixes: readonly string[];
}[] = [
  {
    page: 'training-record',
    apiPrefixes: ['api/training-records', 'api/timekeeping/dispatch'],
  },
];

/**
 * The secret API prefixes active for `pagePath`. A page segment is compared as
 * Angular routes it: the part before a literal `;` (matrix params), decoded,
 * lowercased, so `training-record;x=1` and `training%2Drecord` still match.
 */
const activePublicPageApiPrefixes = (pagePath: string): string[] => {
  const pageSegments = pagePath
    .split('/')
    .filter(Boolean)
    .map((s) => safeDecodeSegment(s.split(';', 1)[0]).toLowerCase());
  return RYN_REPLAY_PUBLIC_PAGE_SECRET_APIS.filter(({ page }) =>
    pageSegments.some(
      (segment, i) => segment === 'public' && pageSegments[i + 1] === page
    )
  ).flatMap(({ apiPrefixes }) => [...apiPrefixes]);
};

/**
 * Public secret pages that still have a request in flight (#34327 review,
 * thread 56127). The page's HttpClient subscription has no teardown, so its
 * error can be logged after the user navigated away: Console text is then
 * scrubbed against every held page as well as the live one. Counted, so two
 * requests from one page need two releases.
 */
const heldSecretPagePaths = new Map<string, number>();

/**
 * Hold `pagePath` for a request opened on it. A non-secret page holds nothing.
 * Returns the release, idempotent, to call when the request ends.
 */
export const holdRynReplaySecretPagePath = (pagePath: string): (() => void) => {
  if (activePublicPageApiPrefixes(pagePath).length === 0) {
    return () => undefined;
  }
  heldSecretPagePaths.set(
    pagePath,
    (heldSecretPagePaths.get(pagePath) ?? 0) + 1
  );
  let released = false;
  return () => {
    if (released) {
      return;
    }
    released = true;
    const count = (heldSecretPagePaths.get(pagePath) ?? 1) - 1;
    if (count > 0) {
      heldSecretPagePaths.set(pagePath, count);
    } else {
      heldSecretPagePaths.delete(pagePath);
    }
  };
};

/**
 * Prefixes for an explicit `pagePath`, or, when none is given, for the live
 * page plus every held one (the Console path).
 */
const secretApiPrefixesFor = (pagePath?: string): string[] =>
  pagePath !== undefined
    ? activePublicPageApiPrefixes(pagePath)
    : [
        ...new Set(
          [
            globalThis.location?.pathname ?? '',
            ...heldSecretPagePaths.keys(),
          ].flatMap(activePublicPageApiPrefixes)
        ),
      ];

/**
 * The URL scrub for a recorded NETWORK request: {@link scrubRynReplayUrl},
 * plus the page-scoped capability APIs above when the request is made from
 * their public page. Without `pagePath`, the live page and every held page
 * apply ({@link holdRynReplaySecretPagePath}).
 */
export const scrubRynReplayNetworkUrl = (
  url: string,
  pagePath?: string
): string => {
  let out = scrubRynReplayUrl(url);
  for (const prefix of secretApiPrefixesFor(pagePath)) {
    out = out.replace(
      new RegExp(`(^|/)(${escapeRegExp(prefix)})(/+)([^/?#]+)`, 'i'),
      (_m, lead: string, api: string, slashes: string) =>
        `${lead}${api}${slashes}${RYN_REPLAY_REDACTED}`
    );
  }
  return out;
};

/**
 * Every URL rule applied to FREE TEXT (console.error message, stack, nested
 * strings): credential path segments, the query allowlist, and, on a public
 * page (live or held), its secret API ids (#34327 review: the page's 404
 * handler logs `/api/training-records/<id>` four ways). Returns the SAME string by
 * reference when nothing matched.
 */
export const scrubRynReplayUrlsInText = (
  text: string,
  pagePath?: string
): string => {
  let out = scrubRynReplayQueryValuesInText(
    scrubRynReplayCredentialPathsInText(text)
  );
  for (const prefix of secretApiPrefixesFor(pagePath)) {
    out = out.replace(
      new RegExp(
        `(^|[/\\s'"(=,\\\\]|%2F)(${escapeRegExp(prefix)})((?:/|%2F)+)((?:(?!%2F)[^/\\s?#'"<>)\\]\`])+)`,
        'gi'
      ),
      (_m, lead: string, api: string, slashes: string, secret: string) =>
        `${lead}${api}${slashes}${RYN_REPLAY_REDACTED}${/[:.,;]+$/.exec(secret)?.[0] ?? ''}`
    );
  }
  if (opaqueSegmentRedaction) {
    // Every piece of a URL-like word (one holding a literal or %2F slash) is
    // swept, the one before the first separator included (a NavigationError
    // path can start with the token, PR 4516 review 5); query and fragment
    // pieces too, fail closed. Whitespace ends the word; `https:` survives
    // because the colon breaks the run.
    out = out.replace(/\S+/g, (word) => {
      if (!/\/|%2F/i.test(word)) {
        return word;
      }
      const parts = word.split(/(\/|%2F|\?|#|%3F|%23)/i);
      for (let k = 0; k < parts.length; k += 2) {
        parts[k] = redactRynReplayOpaqueRuns(parts[k], RYN_REPLAY_REDACTED);
      }
      return parts.join('');
    });
  }
  return out === text ? text : out;
};
